import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { SrIcon } from "@/components/ui/SrIcon";
import { useImpersonation } from "@/contexts/ImpersonationContext";
import { useFeatureFlag } from "@/hooks/use-feature-flag";
import { useChatChannels } from "@/hooks/use-chat-channels";
import { useEnsureHouseMemberships } from "@/hooks/use-chat-house";
import { useUsers, isEmployeeUser } from "@/hooks/use-users";
import { channelKey, filterChannels, type Channel } from "@/lib/chat-room/channels";
import { ChatChannelList } from "@/components/chat-room/ChatChannelList";
import { ChatConversation } from "@/components/chat-room/ChatConversation";
import { NewChatDialog } from "@/components/chat/NewChatDialog";

/**
 * /chat (VIB-3022): kanaler for prosjekter, hele huset og direktemeldinger.
 * Erstatter den flytende chatten når flagget `feature_chat_rom` er på.
 *
 * Chatten bruker alltid den ekte sesjonen (`selfUser`), også under
 * impersonering: chat-RLS autoriserer JWT-en, og meldinger skal aldri sendes
 * i en annens navn.
 */
export default function ChatPage() {
  const enabled = useFeatureFlag("feature_chat_rom");
  const { selfUser } = useImpersonation();
  const selfId = selfUser?.id ?? null;
  const [params, setParams] = useSearchParams();
  const [search, setSearch] = useState("");
  const [newOpen, setNewOpen] = useState(false);

  useEnsureHouseMemberships(selfId, enabled);
  const { channels, groups, membersByKey, isLoading } = useChatChannels(selfId, enabled);
  // Første gang venter vi på uleste før en kanal åpnes (ellers fryses «0 nye»).
  // Senere omlastinger av listen skal ikke lukke samtalen.
  const [ready, setReady] = useState(false);
  useEffect(() => {
    if (!isLoading) setReady(true);
  }, [isLoading]);
  const { data: allUsers } = useUsers();
  const users = useMemo(() => (allUsers ?? []).filter(isEmployeeUser), [allUsers]);
  const avatarById = useMemo(() => new Map(users.map((u) => [u.id, u.avatar_url])), [users]);

  const requestedKey = params.get("kanal");
  const selected: Channel | null =
    channels.find((c) => c.key === requestedKey) ??
    (requestedKey ? null : channels.find((c) => c.unread > 0) ?? groups.flatMap((g) => g.channels)[0] ?? null);

  const select = (key: string) => setParams({ kanal: key }, { replace: true });

  // Uten kanal i URL-en velges første kanal med uleste (ellers den øverste).
  // Valget skrives til URL-en med en gang, så det ikke flytter seg til neste
  // kanal når denne markeres som lest.
  const defaultKey = !requestedKey && ready ? selected?.key ?? null : null;
  useEffect(() => {
    if (defaultKey) setParams({ kanal: defaultKey }, { replace: true });
  }, [defaultKey, setParams]);

  if (!enabled) {
    return (
      <div className="page-container">
        <p className="text-muted-foreground">Ikke tilgjengelig ennå.</p>
      </div>
    );
  }

  return (
    <div className="page-container flex h-full flex-col gap-4 animate-fade-in">
      <div className="flex flex-wrap items-end gap-3">
        <div className="min-w-[240px] flex-1">
          <h1 className="font-display text-[clamp(28px,3vw,34px)] font-semibold tracking-[-0.02em]">Chat</h1>
          <p className="mt-1 text-sm text-muted-foreground">Kanaler per prosjekt, for hele huset og direkte.</p>
        </div>
        <Button onClick={() => setNewOpen(true)} className="gap-2">
          <SrIcon name="plus" className="h-4 w-4" />
          Ny samtale
        </Button>
      </div>

      <div className="flex min-h-[600px] flex-1 flex-wrap overflow-hidden rounded-[10px] border-[1.5px] border-[var(--sr-revemoerk)] bg-card md:flex-nowrap">
        <div className="w-full shrink-0 border-b-[1.5px] border-border md:w-[290px] md:border-b-0 md:border-r-[1.5px]">
          <ChatChannelList
            groups={filterChannels(groups, search)}
            selectedKey={selected?.key ?? null}
            onSelect={(c) => select(c.key)}
            search={search}
            onSearch={setSearch}
            avatarUrlFor={(id) => (id ? avatarById.get(id) ?? null : null)}
            isLoading={!ready}
          />
        </div>
        {selected && selfId && ready ? (
          <ChatConversation
            key={selected.key}
            channel={selected}
            selfId={selfId}
            members={membersByKey.get(selected.key) ?? []}
            users={users}
          />
        ) : (
          <div className="flex flex-1 items-center justify-center p-8 text-sm text-muted-foreground">
            {!ready ? "Henter kanaler …" : requestedKey ? "Fant ikke kanalen, eller du er ikke med i den." : "Velg en kanal."}
          </div>
        )}
      </div>

      {selfId && (
        <NewChatDialog
          open={newOpen}
          onOpenChange={setNewOpen}
          currentUserId={selfId}
          onCreated={(roomId) => {
            setNewOpen(false);
            select(channelKey("room", roomId));
          }}
        />
      )}
    </div>
  );
}
